import { AF_AU, type ClubwiseBrandRules } from './brands.js';
import {
  META_PATTERNS,
  NOT_RECORDED_VALUES,
  PII_MIN_DIGITS,
  STAFF_CONTACT_PATTERNS,
  UK_WEB_JOINER_STAFF_SOURCE_PATTERNS,
  UNTAGGED_META_LABEL,
  UNTAGGED_WEBSITE_LABEL,
  WEB_JOINER_CONTACT_PATTERNS,
  WEBSITE_PATTERNS,
  YEAR_PATTERN,
} from './config.js';

// Pure classification of ClubWise cell values. Shared by the browser preview and
// the server (which re-validates and is the source of truth for what is stored).

/** Trim; the literal "None" (any case) and blank both mean "not recorded" → ''. */
export function cleanCell(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const t = raw.replace(/\s+/g, ' ').trim();
  return NOT_RECORDED_VALUES.includes(t.toLowerCase()) ? '' : t;
}

const norm = (s: string): string => s.replace(/\s+/g, ' ').trim().toLowerCase();

export type StatusClass = 'joined' | 'not_joined' | 'unknown' | 'blank';

/** ⚠️ Status vocabularies are PER BRAND (the UK joined list differs). The default is AU, so every pre-existing caller is unchanged. */
export function classifyStatus(
  status: string,
  rules: Pick<ClubwiseBrandRules, 'joinedStatuses' | 'notJoinedStatuses'> = AF_AU,
): StatusClass {
  const n = norm(status);
  if (n === '') return 'blank';
  if (rules.joinedStatuses.includes(n)) return 'joined';
  if (rules.notJoinedStatuses.includes(n)) return 'not_joined';
  return 'unknown';
}

/**
 * ⚠️ PII GUARD. A Source / Contact Type value holding 8+ digits (a phone number) or an
 * "@" (an email) is not a label at all. It is treated as BLANK, never stored and never
 * logged; the upload preview and the reclassify script report only a COUNT of them.
 */
export function looksLikePii(raw: unknown): boolean {
  if (typeof raw !== 'string') return false;
  return raw.includes('@') || (raw.match(/\d/g)?.length ?? 0) >= PII_MIN_DIGITS;
}

/** cleanCell for a Source / Contact Type: PII removed too. Original case otherwise. */
export function cleanLabelCell(raw: unknown): string {
  return looksLikePii(raw) ? '' : cleanCell(raw);
}

const YEAR_START = new RegExp('^' + YEAR_PATTERN + '\\s+');
const YEAR_END = new RegExp('\\s+' + YEAR_PATTERN + '$');

/**
 * The value as it is MATCHED: lowercase, whitespace collapsed, a numbering prefix
 * ("1-", "7. ", "10 ") and a standalone year (2015-2030) at either end removed.
 * Blank (''), 'none' and punctuation-only values are ''. PII is ''.
 */
export function normaliseForMatch(raw: unknown): string {
  const t = cleanLabelCell(raw);
  let n = norm(t);
  if (n === '' || !/[a-z0-9]/.test(n)) return '';
  n = n.replace(YEAR_START, '').replace(YEAR_END, '');
  // ⚠️ (?!\d): the prefix is one or two digits NOT followed by another, or "2019 ..." loses "20".
  n = n.replace(/^\d{1,2}(?!\d)\s*[.\-]?\s*/, '').trim();
  return n;
}

const anyMatch = (patterns: readonly RegExp[], n: string): boolean => n !== '' && patterns.some((p) => p.test(n));

/** The channel a lead is reported under. Exhaustive: every lead has exactly one. */
export type LeadChannel = 'meta' | 'website' | 'staff' | 'other';

/** The KIND of a digital lead; null = not digital. */
export type DigitalClass = 'meta' | 'website';

/** Display order. */
export const LEAD_CHANNELS: readonly LeadChannel[] = ['meta', 'website', 'staff', 'other'];

export interface DigitalVerdict {
  isDigital: boolean;
  digitalClass: DigitalClass | null;
  channel: LeadChannel;
  /** A website / meta lead whose Source is blank. */
  untagged: boolean;
  /** What the sources breakdown groups by. '' = not recorded. */
  sourceLabel: string;
}

/**
 * Classify one lead (or join) from its Source and Contact Type.
 *
 * PRECEDENCE, first match wins:
 *   1. Contact Type is STAFF            -> staff   (staff always wins, whatever the Source)
 *   2. Contact Type OR Source is META   -> meta    (meta wins over website)
 *   3. Contact Type OR Source is WEBSITE-> website
 *   4. otherwise                        -> other
 */
export function classifyDigital(
  source: string,
  contactType: string,
  rules: Pick<ClubwiseBrandRules, 'webJoinerBySource'> = AF_AU,
): DigitalVerdict {
  const s = cleanLabelCell(source);
  const ns = normaliseForMatch(source);
  const nc = normaliseForMatch(contactType);

  let channel: LeadChannel = 'other';
  if (rules.webJoinerBySource && anyMatch(WEB_JOINER_CONTACT_PATTERNS, nc)) {
    // ⚠️ UK "Web joiner" = HOW they joined, so the Contact Type is IGNORED and the
    // Source decides: staff sources, then Meta, then website, else other.
    if (anyMatch(UK_WEB_JOINER_STAFF_SOURCE_PATTERNS, ns)) channel = 'staff';
    else if (anyMatch(META_PATTERNS, ns)) channel = 'meta';
    else if (anyMatch(WEBSITE_PATTERNS, ns)) channel = 'website';
  } else if (anyMatch(STAFF_CONTACT_PATTERNS, nc)) channel = 'staff';
  else if (anyMatch(META_PATTERNS, nc) || anyMatch(META_PATTERNS, ns)) channel = 'meta';
  else if (anyMatch(WEBSITE_PATTERNS, nc) || anyMatch(WEBSITE_PATTERNS, ns)) channel = 'website';

  const isDigital = channel === 'meta' || channel === 'website';
  const untagged = isDigital && ns === '';
  const sourceLabel = untagged ? (channel === 'meta' ? UNTAGGED_META_LABEL : UNTAGGED_WEBSITE_LABEL) : s;
  return { isDigital, digitalClass: isDigital ? (channel as DigitalClass) : null, channel, untagged, sourceLabel };
}
